/**
 * Making sure the game's typeface is actually loaded before anything draws with it.
 *
 * DOM text is fine on its own: the browser re-renders a node when its @font-face
 * arrives. Canvas text is NOT. PixiJS measures and rasterises a string once, at the
 * moment it builds the texture — if "Terraboma" has not finished loading by then it
 * silently falls back to ui-sans-serif and STAYS that way, because nothing ever
 * redraws it. That is why arena name tags could come up in the browser's default
 * face while every DOM label around them was correct.
 *
 * A font is only fetched when something asks for it, so the boot path asks first.
 */

/** Faces that canvas code draws with — see arena/hero.ts. */
const FACES = ['Terraboma', 'CuteFantasy'];

/**
 * The font stack every PixiJS `Text` must use.
 *
 * Canvas text is the one surface CSS cannot reach: a `fontFamily` on a Pixi TextStyle
 * is a string handed to the canvas rasteriser, so no stylesheet rule, no `game-font`
 * class and no inherited `body` family touches it. Four of these hardcoded
 * `'monospace'` — damage numbers, plot labels, the build ghost and the arena preview —
 * which is why numbers over the world kept coming up in a typewriter face while every
 * DOM label around them was pixel art. Import this instead of writing a literal.
 */
export const CANVAS_FONT = 'Terraboma, CuteFantasy, ui-sans-serif';

/**
 * Resolves once the game faces are loaded, or immediately if the browser has no
 * Font Loading API (jsdom under test). Never rejects and never blocks boot: a font
 * that fails to load is a cosmetic fallback, not a reason to hold the game hostage.
 */
export async function loadGameFonts(timeoutMs = 3000): Promise<void> {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts?.load) return;

  // `load` needs a full font shorthand; the size is irrelevant to which file is
  // fetched, it just has to parse.
  const loads = FACES.map((f) => fonts.load(`16px "${f}"`).catch(() => {}));
  const timeout = new Promise<void>((r) => window.setTimeout(r, timeoutMs));
  await Promise.race([Promise.all(loads).then(() => {}), timeout]);
}
